/**
 * What to look at first, at the top of the Overview.
 *
 * Why the sheet does not balance, and what the filing shows that the model could not
 * explain, used to be somewhere among the import's notes - shown in arrival order, three
 * visible and the rest folded away - and further down, in the diagnosis panel. A user
 * scrolling past thirty notes met the one that mattered last, if at all.
 *
 * The items come from the server, built from the model's own state rather than from the
 * notes' wording, already ordered: the imbalance and the checks where it starts, then
 * totals the filing prints that the model does not reach, then what the filing does not
 * explain - largest first within each. Re-fetched whenever the model changes, so a
 * correction moves them at once. Renders nothing for a model with nothing to look at.
 */

import { useEffect, useState } from 'react';
import { useFinancialModelV2Store } from '../../../store/financial-model-v2';
import type { AttentionItem } from '../../../types/financial-model-v2';
import { formatMoney } from '../../../lib/currency';

const SHOWN = 5;

const GROUPS: Array<{
  kinds: AttentionItem['kind'][];
  heading: string;
  tone: string;
  amountTone: string;
}> = [
  {
    kinds: ['imbalance', 'imbalanceCause'],
    heading: 'Why it does not balance',
    tone: 'border-red-200 bg-red-50',
    amountTone: 'text-red-700',
  },
  {
    kinds: ['printedTotal'],
    heading: 'Totals the filing prints that the model does not reach',
    tone: 'border-amber-200 bg-amber-50',
    amountTone: 'text-amber-800',
  },
  {
    kinds: ['unexplainedShares'],
    heading: 'Not explained by the filing',
    tone: 'border-amber-200 bg-amber-50',
    amountTone: 'text-amber-800',
  },
  {
    // Made, not outstanding: what the accountant's review booked, each with the printed
    // figures it rests on, so nobody has to take it on trust.
    kinds: ['reviewBooking'],
    heading: "Booked by the accountant's review",
    tone: 'border-sky-200 bg-sky-50',
    amountTone: 'text-sky-800',
  },
];

function Group({
  heading,
  items,
  tone,
  amountTone,
  currency,
}: {
  heading: string;
  items: AttentionItem[];
  tone: string;
  amountTone: string;
  currency: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? items : items.slice(0, SHOWN);
  const hidden = items.length - SHOWN;
  return (
    <section className={`rounded-xl border px-4 py-3 ${tone}`} aria-label={heading}>
      <h3 className="text-sm font-semibold text-gray-900">
        {heading}
        <span className="ml-1 font-normal text-gray-600">({items.length})</span>
      </h3>
      <ul className="mt-2 divide-y divide-black/5">
        {shown.map((item, index) => (
          <li key={`${item.kind}-${item.period}-${index}`} className="py-1.5">
            <div className="flex items-baseline justify-between gap-3">
              <span
                className={
                  item.kind === 'imbalance'
                    ? 'text-sm font-medium text-gray-900'
                    : 'text-sm text-gray-900'
                }
              >
                {item.title}
              </span>
              <span className={`shrink-0 font-mono text-sm tabular-nums ${amountTone}`}>
                {formatMoney(item.amount, currency)}
              </span>
            </div>
            {item.detail && <div className="mt-0.5 text-xs text-gray-600">{item.detail}</div>}
          </li>
        ))}
      </ul>
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className="mt-1 text-xs font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
        >
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </section>
  );
}

export function AttentionPanel() {
  const model = useFinancialModelV2Store((s) => s.model);
  const attention = useFinancialModelV2Store((s) => s.attention);
  const loadAttention = useFinancialModelV2Store((s) => s.loadAttention);

  // A list describing figures the user has since corrected is worse than none.
  useEffect(() => {
    void loadAttention();
  }, [loadAttention, model?.version]);

  if (!model || !attention || attention.length === 0) return null;
  const currency = model.settings.baseCurrency;

  return (
    <div className="mb-6 space-y-3" data-testid="attention-panel">
      <h2 className="text-base font-semibold text-gray-900">Needs attention</h2>
      {GROUPS.map((group) => {
        const items = attention.filter((item) => group.kinds.includes(item.kind));
        return items.length ? (
          <Group
            key={group.heading}
            heading={group.heading}
            items={items}
            tone={group.tone}
            amountTone={group.amountTone}
            currency={currency}
          />
        ) : null;
      })}
    </div>
  );
}
